import { newOutcome } from '../storyDefaults';
import type { BeatChainDef, BeatDef, OptionDef, OutcomeDef, OutcomeTableCase } from './types';

/**
 * Pure edits on a beat chain for the beat editor's outline: add a beat, option, case or outcome, and rename a beat, table or outcome
 * with every reference updated (root, outcome `next`, option `table`, table picks, cutscene `outcome`). Each returns a new chain.
 */

/** The first `${base}${n}` (n from 1) that isn't taken. */
export function freeId(taken: Iterable<string>, base: string): string {
  const set = new Set(taken);
  let n = 1;
  while (set.has(`${base}${n}`)) n++;
  return `${base}${n}`;
}

const clone = (c: BeatChainDef): BeatChainDef => structuredClone(c);

/** A new choice beat with two options, each with its own table and outcome. Returns the chain and the beat id. */
export function addBeat(chain: BeatChainDef, from?: { outcomeId: string }): [BeatChainDef, string] {
  const c = clone(chain);
  c.beats ??= {};
  c.tables ??= {};
  c.outcomes ??= {};
  const id = freeId(Object.keys(c.beats), 'b');
  const options: OptionDef[] = ['A', 'B'].map((o) => {
    const table = freeId(Object.keys(c.tables!), `t${id}${o}_`);
    const outcome = freeId(Object.keys(c.outcomes!), `o${id}${o}_`);
    c.tables![table] = [{ else: true, pick: [{ w: 1, outcome }] }];
    c.outcomes![outcome] = newOutcome(o === 'A' ? 'good' : 'bad');
    return { id: o, label: `Option ${o}`, table, toneNote: '', requiresLabel: '' };
  });
  c.beats[id] = {
    cells: [],
    choice: { prompt: '', timerS: null, timeout: '', options },
    outcome: '',
  };
  if (!c.root) c.root = id;
  if (from && c.outcomes[from.outcomeId]) c.outcomes[from.outcomeId]!.next = id;
  return [c, id];
}

/** Adds an option (up to 3) with a fresh table and outcome. */
export function addOption(chain: BeatChainDef, beatId: string): BeatChainDef {
  const c = clone(chain);
  const choice = c.beats?.[beatId]?.choice;
  if (!choice) return chain;
  const options = (choice.options ??= []);
  if (options.length >= 3) return chain;
  const id =
    ['A', 'B', 'C'].find((x) => !options.some((o) => o.id === x)) ??
    freeId(
      options.map((o) => o.id ?? ''),
      'O',
    );
  c.tables ??= {};
  c.outcomes ??= {};
  const table = freeId(Object.keys(c.tables), `t${beatId}${id}_`);
  const outcome = freeId(Object.keys(c.outcomes), `o${beatId}${id}_`);
  c.tables[table] = [{ else: true, pick: [{ w: 1, outcome }] }];
  c.outcomes[outcome] = newOutcome('mixed');
  options.push({ id, label: `Option ${id}`, table, toneNote: '', requiresLabel: '' });
  return c;
}

/** Removes an option (keeping at least... nothing: the validator reports fewer than 2). Its table and outcomes stay until unused. */
export function removeOption(chain: BeatChainDef, beatId: string, index: number): BeatChainDef {
  const c = clone(chain);
  const choice = c.beats?.[beatId]?.choice;
  if (!choice?.options) return chain;
  const [gone] = choice.options.splice(index, 1);
  if (gone && choice.timeout === gone.id) choice.timeout = choice.options[0]?.id ?? '';
  return c;
}

/** Adds a case before the table's final else (a condition case picking the first outcome). */
export function addCase(chain: BeatChainDef, tableId: string): BeatChainDef {
  const c = clone(chain);
  const cases = (c.tables ??= {})[tableId] ?? [];
  const first = cases.flatMap((k) => k.pick ?? [])[0]?.outcome ?? '';
  const k: OutcomeTableCase = {
    if: { fact: 'var.', op: 'eq', value: 'true', args: [], values: [], all: [], any: [] },
    else: false,
    pick: [{ w: 1, outcome: first }],
  };
  const elseAt = cases.findIndex((x) => x.else);
  if (elseAt >= 0) cases.splice(elseAt, 0, k);
  else cases.push(k);
  c.tables![tableId] = cases;
  return c;
}

/** A new outcome; returns the chain and its id. */
export function addOutcome(
  chain: BeatChainDef,
  base = 'o',
  tone = 'mixed',
): [BeatChainDef, string] {
  const c = clone(chain);
  c.outcomes ??= {};
  const id = freeId(Object.keys(c.outcomes), base);
  c.outcomes[id] = newOutcome(tone);
  return [c, id];
}

/** A new table with one else case; returns the chain and its id. */
export function addTable(chain: BeatChainDef, outcomeId: string): [BeatChainDef, string] {
  const c = clone(chain);
  c.tables ??= {};
  const id = freeId(Object.keys(c.tables), 't');
  c.tables[id] = [{ else: true, pick: [{ w: 1, outcome: outcomeId }] }];
  return [c, id];
}

const renameKey = <T>(rec: Record<string, T> | undefined, from: string, to: string) => {
  if (!rec || !(from in rec) || from === to) return rec;
  return Object.fromEntries(Object.entries(rec).map(([k, v]) => [k === from ? to : k, v]));
};

export type RenameKind = 'beat' | 'table' | 'outcome';

/** Renames a beat, table or outcome and updates every reference. Refuses (returns the chain) when the new id is taken or blank. */
export function rename(
  chain: BeatChainDef,
  kind: RenameKind,
  from: string,
  to: string,
): BeatChainDef {
  const key = kind === 'beat' ? 'beats' : kind === 'table' ? 'tables' : 'outcomes';
  const rec = chain[key] as Record<string, unknown> | undefined;
  if (!to || from === to || !rec || !(from in rec) || to in rec) return chain;
  const c = clone(chain);
  if (kind === 'beat') {
    c.beats = renameKey(c.beats, from, to) as Record<string, BeatDef>;
    if (c.root === from) c.root = to;
    for (const o of Object.values(c.outcomes ?? {})) if (o.next === from) o.next = to;
  } else if (kind === 'table') {
    c.tables = renameKey(c.tables, from, to) as Record<string, OutcomeTableCase[]>;
    for (const b of Object.values(c.beats ?? {}))
      for (const o of b.choice?.options ?? []) if (o.table === from) o.table = to;
  } else {
    c.outcomes = renameKey(c.outcomes, from, to) as Record<string, OutcomeDef>;
    for (const cases of Object.values(c.tables ?? {}))
      for (const k of cases) for (const p of k.pick ?? []) if (p.outcome === from) p.outcome = to;
    for (const b of Object.values(c.beats ?? {})) if (b.outcome === from) b.outcome = to;
  }
  return c;
}

/** Deletes a beat, table or outcome and clears references to it (they then show as validator errors to fix). */
export function remove(chain: BeatChainDef, kind: RenameKind, id: string): BeatChainDef {
  const c = clone(chain);
  if (kind === 'beat') {
    delete c.beats?.[id];
    if (c.root === id) c.root = '';
    for (const o of Object.values(c.outcomes ?? {})) if (o.next === id) o.next = '';
  } else if (kind === 'table') delete c.tables?.[id];
  else delete c.outcomes?.[id];
  return c;
}

/** Beats in outline order: depth-first from the root, then anything unreachable. */
export function beatOrder(chain: BeatChainDef): string[] {
  const out: string[] = [];
  const visit = (id: string) => {
    const b = chain.beats?.[id];
    if (!b || out.includes(id)) return;
    out.push(id);
    const outs = b.choice
      ? (b.choice.options ?? []).flatMap((o) =>
          (chain.tables?.[o.table ?? ''] ?? []).flatMap((k) =>
            (k.pick ?? []).map((p) => p.outcome ?? ''),
          ),
        )
      : [b.outcome ?? ''];
    for (const o of outs) {
      const next = chain.outcomes?.[o]?.next;
      if (next) visit(next);
    }
  };
  if (chain.root) visit(chain.root);
  for (const id of Object.keys(chain.beats ?? {})) if (!out.includes(id)) out.push(id);
  return out;
}
